import React, { useEffect, useState, useMemo, useCallback } from "react";
import "./css/common.css";
import "../staff/css/dashboard-premium.css";
import { Box, Typography } from "@mui/material";
import { BarChart, LineChart, PieChart, ScatterChart } from "@mui/x-charts";
import { Card, Row } from "antd";
import { LineChartOutlined, FileTextOutlined } from "@ant-design/icons";
import { post } from "../services/services";
import { ApiURL, baseURL, S3_BASE_URL } from "../services/apiConstants";
import { ErrorToast, SuccessToast } from "../widgets/AppToast";
import { useNavigate } from "react-router-dom";
import GenerateReportTool from "../components/GenerateReportTool";
import { GENERATEREPORT } from "../utils/downloadPdf";
import DateEngineChartSelectorCompany from "../components/DateEngineChartSelectorCompany";
import dayjs from "dayjs";
import useUserData from "../hooks/useUserData";
const { Meta } = Card;

function CompanyDashboard() {
  const navigate = useNavigate();
  const [selectedDates, setSelectedDates] = useState(null);
  const [engineType, setEngineType] = useState("");
  const [selectedChart, setSelectedChart] = useState("bar");
  const [selectedParameters, setSelectedParameters] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [series, setSeries] = useState([]);
  const [xAxis, setXAxis] = useState([]);
  const [shipTableList, setShipTableList] = useState([]);
  const [shipId, setShipId] = useState("");
  const [shipDetailsList, setShipDetailsList] = useState([]);
  const [voyage, setVoyage] = useState("");
  const [selectedReport, setSelectedReport] = useState("ORB Part 1");
  const [filteredData, setFilteredData] = useState([]);
  const [timeFormat, setTimeFormat] = useState("daily");
  const [engineOptions, setEngineOptions] = useState([]);
  const [parameterOptions, setParameterOptions] = useState({});
  const [selectedLogbook, setSelectedLogbook] = useState(["engine_logbook"]);

  /** ----------------- API HANDLERS ----------------- **/

  const fetchShipDetailsList = useCallback(async () => {
    try {
      const res = await post(ApiURL.dashboard_ship_details_com, new FormData());
      if (res?.status) setShipDetailsList(res.ship_list || []);
    } catch (error) {
      console.error("Error fetching ship list:", error);
    }
  }, []);

  const fetchShipTableList = useCallback(async () => {
    try {
      const res = await post(ApiURL.list_ships_view_com, new FormData());
      if (res?.status && res.ships?.length) {
        setShipTableList(res.ships);
        setShipId(res.ships[0].id);
        // auto select first ship
      }
    } catch (error) {
      console.error("Error fetching ship table list:", error);
    }
  }, []);

  const fetchVoyageList = useCallback(async (shipId) => {
    if (!shipId) return;
    try {
      const formData = new FormData();
      // formData.append("ship_id", shipId);
      formData.append("ship_id", Number(shipId));
      const res = await post(ApiURL.list_voyage_info_com, formData);
      if (res?.status) {
        setFilteredData(res.voyage || []);
      } else {
        ErrorToast(
          "Error Message",
          res?.response?.data?.message || "Failed to fetch voyage list",
        );
      }
    } catch (error) {
      console.error("Error fetching voyage list:", error);
    }
  }, []);

  const fetchGraphData = useCallback(async () => {
    if (!startDate || !endDate || !shipId || !engineType) return;
    try {
      const formData = new FormData();
      formData.append("start_date", startDate);
      formData.append("end_date", endDate);
      formData.append("machines", JSON.stringify(selectedParameters));
      formData.append("log_book", JSON.stringify(selectedLogbook));
      formData.append("ship_id", shipId);

      const res = await post(ApiURL.company_get_grap_data_com, formData);
      if (res?.status) {
        SuccessToast("Success Message !", res?.message);
        setSeries(res.data?.series || []);
        setXAxis(res.data?.xAxis || []);
      } else {
        console.error("Error: Response status is false.");
      }
    } catch (error) {
      console.error("API call error:", error);
    }
  }, [startDate, endDate, selectedParameters, selectedLogbook]);

  const fetchEngineOptions = useCallback(async (shipId) => {
    if (!shipId) return;
    try {
      const formData = new FormData();
      formData.append("ship_id", shipId);
      post(ApiURL.all_machinary_on_ship_com, formData).then((res) => {
        if (res?.status) {
          const options = Object.entries(res.data || {}).flatMap(([_, items]) =>
            items.map((item) => ({
              value: item.type,
              label: item.name,
            })),
          );
          if (options.length) {
            setEngineOptions(options);
            fetchParameterOptions([options[0].value], shipId);
          }
        }
      });
    } catch (error) {
      console.log("error message ********", error);
    }
  }, []);

  const fetchParameterOptions = useCallback(() => {
    const formData = new FormData();
    formData.append("ship_id", shipId);

    let mergedTypes = [];

    if (selectedLogbook.includes("engine_logbook")) {
      mergedTypes = [...engineType, ...selectedLogbook];
    } else {
      mergedTypes = [...selectedLogbook];
    }

    const filteredTypes = mergedTypes.filter(
      (item) => item !== "engine_logbook",
    );
    formData.append("types", JSON.stringify(filteredTypes));

    post(ApiURL.machine_fields_graph_com, formData).then((res) => {
      if (res?.status) {
        setParameterOptions(res.data || {});
      }
    });
  }, [shipId, engineType, selectedLogbook]);

  useEffect(() => {
    if (engineType && engineType.length > 0) {
      fetchParameterOptions();
    }
  }, [JSON.stringify(engineType), JSON.stringify(selectedLogbook)]);

  /** ----------------- DATA TRANSFORM ----------------- **/

  const transformDataByFormat = (rawXAxis, rawSeries, format) => {
    if (!rawXAxis?.[0]?.data || !rawSeries?.length)
      return { xAxis: [], series: [] };

    const parsedDates = rawXAxis[0].data.map((d) => dayjs(d));
    let groups = {};

    parsedDates.forEach((date, idx) => {
      let key =
        format === "weekly"
          ? `${date.year()}-W${date.isoWeek()}`
          : format === "monthly"
            ? date.format("YYYY-MM")
            : date.format("YYYY-MM-DD");

      if (!groups[key]) groups[key] = [];
      groups[key].push(idx);
    });

    const newXAxis = Object.keys(groups);
    const newSeries = rawSeries.map((serie) => ({
      ...serie,
      data: newXAxis.map((group) => {
        const indices = groups[group];
        const values = indices.map((i) => serie.data[i] || 0);
        return values.length
          ? values.reduce((a, b) => a + b, 0) / values.length
          : 0;
      }),
    }));

    return {
      xAxis: [{ scaleType: "band", data: newXAxis }],
      series: newSeries,
    };
  };

  const { xAxis: transformedXAxis, series: transformedSeries } = useMemo(
    () => transformDataByFormat(xAxis, series, timeFormat),
    [xAxis, series, timeFormat],
  );

  const pieChartData = useMemo(
    () =>
      transformedSeries.map((item, index) => ({
        id: index,
        value: item.data.reduce((a, b) => a + b, 0),
        label: item.label,
      })),
    [transformedSeries],
  );

  const scatterSeries = useMemo(
    () =>
      transformedSeries.map((item) => ({
        label: item.label,
        color: item.color,
        data: item.data.map((value, index) => ({ x: index, y: value })),
      })),
    [transformedSeries],
  );

  /** ----------------- EFFECTS ----------------- **/

  useEffect(() => {
    fetchShipDetailsList();
    fetchShipTableList();
  }, []);

  useEffect(() => {
    if (shipId) {
      fetchVoyageList(shipId);
      fetchEngineOptions(shipId);
    }
  }, [shipId]);

  // useEffect(() => {
  //   fetchGraphData();
  // }, [fetchGraphData]);

  /** ----------------- PDF GENERATION ----------------- **/
  const generatePdf = useCallback(() => {
    GENERATEREPORT(
      ApiURL.generate_report_com,
      voyage,
      startDate,
      endDate,
      selectedReport,
    );
  }, [voyage, startDate, endDate, selectedReport]);

  const user = useUserData();
  const companyName = user?.data?.name || user?.data?.company_name || "Admin";

  /** ----------------- RENDER ----------------- **/
  return (
    <div className="premium-dashboard-bg" style={{ padding: "24px" }}>
      {/* Welcome Banner */}
      <div
        className="dashboard-welcome-banner fade-in-section"
        style={{ marginBottom: "24px" }}
      >
        <div className="welcome-banner-content">
          <div className="welcome-banner-left">
            <h1>👋 Welcome back, {companyName}</h1>
            <p>Your fleet management overview at a glance</p>
          </div>
          <div className="welcome-banner-right">
            <span className="designation-badge">Company Admin</span>
            <span className="date-badge">
              📅 {dayjs().format("DD MMM YYYY")}
            </span>
          </div>
        </div>
      </div>

      {/* --- Ship Cards --- */}
      <div className="fade-in-section">
        <div className="section-header">
          <span className="section-icon" style={{ fontSize: "20px" }}>
            🚢
          </span>
          <h3>Fleet Overview</h3>
        </div>
        <Card
          className="hover-lift-card"
          style={{
            marginBottom: "24px",
            borderRadius: "20px",
            boxShadow: "0 10px 40px -10px rgba(0,0,0,0.08)",
            border: "1px solid rgba(255,255,255,0.4)",
            overflow: "hidden",
            background: "rgba(255,255,255,0.85)",
          }}
          bodyStyle={{ padding: "24px" }}
        >
          <Row gutter={[16, 16]} justify="space-between">
            <div
              style={{
                display: "flex",
                overflowX: "auto",
                padding: "10px 0",
                gap: "16px",
              }}
            >
              {shipDetailsList.map((card) => (
                <div key={card.id} style={{ minWidth: "280px", flexShrink: 0 }}>
                  <Card
                    hoverable
                    style={{
                      width: "100%",
                      maxWidth: "360px",
                      height: "270px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                      overflow: "hidden",
                      borderRadius: "16px",
                      border: "1px solid #e2e8f0",
                      transition: "all 0.3s ease",
                    }}
                    onClick={() => navigate(`/edit-ship/${btoa(card.id)}`)}
                    cover={
                      <div
                        style={{
                          position: "relative",
                          height: "170px",
                          overflow: "hidden",
                        }}
                      >
                        <img
                          className="hero-image-zoom"
                          alt={card.vessel_name}
                          src={
                            card.image_ship && card.image_ship.length > 0
                              ? `${S3_BASE_URL}${Array.isArray(card.image_ship) ? card.image_ship[0] : card.image_ship}`
                              : "/assets/images/default-ship.png"
                          }
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/assets/images/default-ship.png";
                          }}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: "60px",
                            background:
                              "linear-gradient(transparent, rgba(0,0,0,0.5))",
                            pointerEvents: "none",
                          }}
                        />
                      </div>
                    }
                  >
                    <Meta
                      title={
                        <span
                          style={{
                            fontWeight: 700,
                            color: "#0f172a",
                            fontSize: "14px",
                          }}
                        >
                          {card.vessel_name}
                        </span>
                      }
                      description={
                        <span style={{ color: "#64748b", fontSize: "12px" }}>
                          🏳️ Flag: {card.flag}
                        </span>
                      }
                      style={{ textAlign: "center" }}
                    />
                  </Card>
                </div>
              ))}
            </div>
          </Row>
        </Card>
      </div>

      {/* --- Charts --- */}
      <div className="fade-in-section">
        <div className="section-header">
          <LineChartOutlined
            className="section-icon"
            style={{ color: "#E11236" }}
          />
          <h3>Performance Analytics</h3>
        </div>
        <Card
          className="chart-card hover-lift-card"
          bodyStyle={{ padding: "28px" }}
        >
          <Box sx={{ padding: 1, flex: 1 }}>
            <DateEngineChartSelectorCompany
              selectedDates={selectedDates}
              setSelectedDates={setSelectedDates}
              engineType={engineType}
              setEngineType={setEngineType}
              selectedChart={selectedChart}
              setSelectedChart={setSelectedChart}
              selectedParameters={selectedParameters}
              setSelectedParameters={setSelectedParameters}
              ship_table_list={shipTableList}
              set_ship_id={setShipId}
              ship_id={shipId}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              startDate={startDate}
              endDate={endDate}
              Graph_data={fetchGraphData}
              timeFormat={timeFormat}
              setTimeFormat={setTimeFormat}
              engineOptions={engineOptions}
              parameterMappings={parameterOptions}
              setSelectedLogbook={setSelectedLogbook}
              selectedLogbook={selectedLogbook}
            />

            <Box
              sx={{
                width: "100%",
                height: "480px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                marginTop: 3,
              }}
            >
              <Typography
                variant="h6"
                align="center"
                gutterBottom
                style={{
                  fontWeight: 700,
                  color: "#0f172a",
                  textTransform: "capitalize",
                }}
              >
                {selectedChart} Chart ({timeFormat})
              </Typography>

              {selectedChart === "bar" && (
                <BarChart
                  height={410}
                  margin={{ top: 80, bottom: 30, left: 40, right: 40 }}
                  series={transformedSeries}
                  xAxis={transformedXAxis}
                  slotProps={{ legend: { hidden: true } }}
                />
              )}
              {selectedChart === "line" && (
                <LineChart
                  height={410}
                  margin={{ top: 80, bottom: 30, left: 40, right: 40 }}
                  series={transformedSeries}
                  xAxis={transformedXAxis}
                  slotProps={{ legend: { hidden: true } }}
                />
              )}
              {selectedChart === "pie" && (
                <PieChart
                  height={410}
                  margin={{ top: 50, bottom: 30, left: -70 }}
                  series={[{ data: pieChartData }]}
                  slotProps={{ legend: { hidden: true } }}
                />
              )}
              {selectedChart === "scatter" && (
                <ScatterChart
                  height={410}
                  margin={{ top: 80, bottom: 30, left: 40, right: 40 }}
                  series={scatterSeries}
                  slotProps={{ legend: { hidden: true } }}
                />
              )}
            </Box>
          </Box>
        </Card>
      </div>

      {/* --- Report Generator --- */}
      <div className="fade-in-section" style={{ marginTop: "24px" }}>
        <div className="section-header">
          <FileTextOutlined
            className="section-icon"
            style={{ color: "#E11236" }}
          />
          <h3>Generate Report</h3>
        </div>
        <Card
          className="report-card hover-lift-card"
          bodyStyle={{ padding: "28px" }}
        >
          <GenerateReportTool
            selectedDates={selectedDates}
            setSelectedDates={setSelectedDates}
            voyage={voyage}
            setVoyage={setVoyage}
            selectedReport={selectedReport}
            setselectedReport={setSelectedReport}
            set_ship_ids={setShipId}
            ship_ids={shipId}
            setStartDates={setStartDate}
            setEndDates={setEndDate}
            ship_table_list={shipTableList}
            voyage_list={fetchVoyageList}
            filteredData={filteredData}
            generatePdf={generatePdf}
          />
        </Card>
      </div>
    </div>
  );
}

export default CompanyDashboard;
